import React from 'react';

// A TIME-OF-DAY FIELD IN THE ONE CLOCK FORMAT (owner, 15 Oct 2026): hour 1–12,
// minute, AM/PM — instead of <input type="time">, which draws 12- or 24-hour
// by the browser's locale (an en-GB laptop showed "14:30" beside screens that
// say "2:30 PM").
//
// The VALUE stays 24-hour "HH:mm" in and out, exactly what <input type="time">
// gave, so the form state, its comparisons and the payload do not change.
// '' (nothing chosen) is allowed until an hour is picked.

const pad2 = (n) => String(n).padStart(2, '0');
const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

function parse(value) {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(value || ''));
    if (!m || Number(m[1]) > 23) return null;
    const h24 = Number(m[1]);
    return { h12: h24 % 12 === 0 ? 12 : h24 % 12, minute: Number(m[2]), meridiem: h24 < 12 ? 'AM' : 'PM' };
}

export default function TimeSelect({
    value,
    onChange,
    minuteStep = 5,
    disabled = false,
    id,
    testId,
    ariaLabel,
    className = '',
    selectClassName = 'tt-select p-2 border border-gray-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] disabled:opacity-50',
}) {
    const cur = parse(value);
    const minutes = [];
    for (let m = 0; m < 60; m += minuteStep) minutes.push(m);
    // A stored minute off the step (09:07) stays selectable rather than being
    // silently moved.
    if (cur && !minutes.includes(cur.minute)) minutes.push(cur.minute);
    minutes.sort((a, b) => a - b);

    const emit = (next) => {
        const h12 = next.h12 ?? cur?.h12;
        if (!h12) return;
        const minute = next.minute ?? cur?.minute ?? 0;
        const meridiem = next.meridiem ?? cur?.meridiem ?? 'AM';
        const h24 = (Number(h12) % 12) + (meridiem === 'PM' ? 12 : 0);
        onChange?.(`${pad2(h24)}:${pad2(minute)}`);
    };

    return (
        <div className={`inline-flex items-center gap-1.5 ${className}`} data-testid={testId} role="group" aria-label={ariaLabel}>
            <select
                id={id}
                data-testid={testId ? `${testId}-hour` : undefined}
                aria-label={ariaLabel ? `${ariaLabel} – hour` : 'Hour'}
                value={cur ? String(cur.h12) : ''}
                disabled={disabled}
                onChange={(e) => emit({ h12: Number(e.target.value) })}
                className={selectClassName}
            >
                {!cur && <option value="">--</option>}
                {HOURS.map((h) => <option key={h} value={String(h)}>{h}</option>)}
            </select>
            <span className="text-gray-400">:</span>
            <select
                data-testid={testId ? `${testId}-minute` : undefined}
                aria-label={ariaLabel ? `${ariaLabel} – minute` : 'Minute'}
                value={cur ? String(cur.minute) : ''}
                disabled={disabled || !cur}
                onChange={(e) => emit({ minute: Number(e.target.value) })}
                className={selectClassName}
            >
                {!cur && <option value="">--</option>}
                {minutes.map((m) => <option key={m} value={String(m)}>{pad2(m)}</option>)}
            </select>
            <select
                data-testid={testId ? `${testId}-meridiem` : undefined}
                aria-label={ariaLabel ? `${ariaLabel} – AM or PM` : 'AM or PM'}
                value={cur ? cur.meridiem : 'AM'}
                disabled={disabled || !cur}
                onChange={(e) => emit({ meridiem: e.target.value })}
                className={selectClassName}
            >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
        </div>
    );
}
